import Swiper from 'swiper';
import EffectMaterial from './effect-material.esm.js';
import { EffectFade, Navigation, Pagination, Thumbs, Scrollbar, Autoplay, FreeMode } from 'swiper/modules';

class SwiperCarousel {
	constructor(config) {
		this.config = Object.assign({ scope: '.swiper' }, config);
		this.elements = document.querySelectorAll(this.config.scope);

		if (this.elements.length > 0) {
			this.init();
		}
	}

	init() {
		// Normale swipers eerst, daarna delayed (zodat thumb-swipers al bestaan)
		this.#initializeBySelector(`${this.config.scope}:not(.delay-init)`);
		this.#initializeBySelector(`${this.config.scope}.delay-init`);
	}

	#initializeBySelector(selector) {
		document.querySelectorAll(selector).forEach(el => this.#initialiseSingleObject(el));
	}

	#initialiseSingleObject(element) {
		const settings = this.#buildSettings(element);
		const container = element.closest(settings.wrapperEl);

		if (settings.minSlides > 0) {
			this.#cloneSlides(element, settings.minSlides);
		}

		const swiper = this.#createSwiper(element, container, settings);

		this.#hideNavigationIfNeeded(element, swiper, settings);

		if (settings.autoplay && settings.autoplayInView !== false) {
			this.#observeAutoplay(element, swiper);
		}

		if (container) {
			this.#bindSliderControl(container, swiper);
			this.#bindSlideTitles(container, swiper);
		}
	}

	// --- Settings ---

	#buildSettings(element) {
		const { config, ...data } = this.#getDataAttributes(element);

		let settings = Object.assign(this.#defaultSettings(), data);

		// data-config (JSON-object) overschrijft losse data-* attributen
		if (config) {
			if (typeof config === 'object') {
				settings = { ...settings, ...config };
			} else {
				console.error('Invalid data-config JSON:', config);
			}
		}

		this.#buildAutoplaySettings(settings);
		this.#buildThumbsSettings(settings);
		this.#buildEffectMaterialSettings(settings);

		return settings;
	}

	#buildAutoplaySettings(settings) {
		if (!settings.delay) return;

		settings.autoplay = {
			delay: settings.delay,
			pauseOnMouseEnter: settings.pauseOnMouseEnter !== false,
			disableOnInteraction: false,
			reverseDirection: settings.reverse === true,
		};
	}

	#buildThumbsSettings(settings) {
		if (!settings.thumbs?.swiper) return;

		const thumbElement = document.querySelector(settings.thumbs.swiper);
		const thumbSwiper = thumbElement?.swiper;

		// Geen geïnitialiseerde thumb-swiper gevonden -> thumbs weglaten (anders crasht Swiper)
		if (!thumbSwiper) {
			delete settings.thumbs;
			return;
		}

		settings.thumbs.swiper = thumbSwiper;

		const buffer = parseInt(thumbElement.dataset.thumbBuffer ?? 1, 10);
		const bufferNext = parseInt(thumbElement.dataset.thumbBufferNext ?? buffer, 10);
		const bufferPrev = parseInt(thumbElement.dataset.thumbBufferPrev ?? buffer, 10);
		let prevIndex = 0;

		settings.on = {
			...settings.on,
			slideChange() {
				const offset = this.activeIndex > prevIndex ? bufferNext : bufferPrev;
				thumbSwiper.slideTo(Math.max(0, this.activeIndex - offset));
				prevIndex = this.activeIndex;
			},
		};
	}

	#buildEffectMaterialSettings(settings) {
		if (settings.effect !== 'material') return;

		settings.modules = [...settings.modules, EffectMaterial];
		settings.speed = 1000;
		settings.materialEffect = {
			slideSplitRatio: 0.5,
		};
		settings.slideToClickedSlide = true;
		settings.grabCursor = false;
		settings.spaceBetween = 20;
		settings.slidesPerView = 2;
		settings.breakpoints = {
			640: {
				slidesPerView: 2,
			},
			768: {
				slidesPerView: 2,
			},
			1400: {
				slidesPerView: 2,
				spaceBetween: 40,
			},
		};
	}

	#getDataAttributes(element) {
		const data = {};

		// Converteer alle data-* attributes naar camelCase object
		Array.from(element.attributes).forEach(attr => {
			if (!attr.name.startsWith('data-')) return;

			const key = attr.name
				.substring(5)
				.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

			try {
				data[key] = JSON.parse(attr.value);
			} catch {
				data[key] = attr.value;
			}
		});

		return data;
	}

	#defaultSettings() {
		return {
			wrapperEl: '.swiper-container',
			modules: [Navigation, Thumbs, Pagination, EffectFade, Scrollbar, Autoplay, FreeMode],
			slidesPerView: 'auto',
			grabCursor: true,
			watchSlidesProgress: true,
			navigation: {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
				addIcons: false,
			},
		};
	}

	// --- Swiper creation + scoped controls ---

	#createSwiper(element, container, settings) {
		if (container) {
			this.#buildScopedControls(container, settings);
		} else {
			// Zonder container-id kunnen we geen unieke selectors maken;
			// globale nav-selectors zouden élke slider besturen -> navigatie weglaten
			delete settings.navigation;
		}

		const swiper = new Swiper(element, settings);

		if (settings.fanEffect) {
			this.#attachFanEffect(element, swiper, settings);
		}

		return swiper;
	}

	// --- Fan / waaier effect -----------------------------------------------
	// Echte cirkel-geometrie: elke kaart staat op een hoek (theta) op een cirkel
	// met straal `radius`. De boog (arcY) volgt PUUR uit die hoek (R*(1-cos theta)),
	// en de kanteling van de kaart is exact diezelfde hoek (tiltRatio=1 = tangent
	// aan de cirkel) -- dus boogdiepte en kanteling horen ALTIJD 1-op-1 bij elkaar.
	// Dat is bewust: als je de kanteling loskoppelt van de boog (tiltRatio < 1)
	// lijkt het net of de kaartjes willekeurig los van elkaar op en neer floaten
	// in plaats van keurig achter elkaar een ronde baan te belopen.
	//
	// Draait continu mee tijdens de (linear) marquee-transition via een rAF-loop
	// (event-gedreven herberekenen update maar één keer per swiper-stap, waardoor
	// kaartjes tussen twee stappen in stil zouden blijven staan). Om layout-
	// thrashing te voorkomen worden per frame eerst ALLE posities gelezen en pas
	// daarna geschreven.
	//
	// Config via losse attributen of via het fanEffect-object (data-config):
	//   data-fan-effect="true"
	//   data-fan-theta="28"       hoek (graden) van de rand-kaart op de cirkel
	//   data-fan-radius="700"     straal van de cirkel in px -- kleiner rondje = scherpere boog
	//   data-fan-tilt-ratio="1"   1 = kanteling volgt de boog 1-op-1 (aanbevolen);
	//                             lager ontkoppelt kanteling van boogdiepte weer
	//   data-fan-invert="false"   draairichting omkeren
	#attachFanEffect(element, swiper, settings) {
		const cfg = (settings.fanEffect && typeof settings.fanEffect === 'object') ? settings.fanEffect : {};

		const thetaMax  = Number(cfg.theta ?? settings.fanTheta ?? 28);
		const radius    = Number(cfg.radius ?? settings.fanRadius ?? 700);
		const tiltRatio = Number(cfg.tiltRatio ?? settings.fanTiltRatio ?? 1);
		const sign      = (cfg.invert ?? settings.fanInvert) ? -1 : 1;
		const selector  = cfg.selector ?? settings.fanSelector ?? '.team-card';

		const thetaMaxRad = thetaMax * Math.PI / 180;

		let rafId = null;
		let running = false;

		const tick = () => {
			const rect = element.getBoundingClientRect();
			const center = rect.left + rect.width / 2;
			const half = rect.width / 2 || 1;

			// Pass 1: alleen lezen (getBoundingClientRect) -- geen writes ertussen.
			const updates = swiper.slides.map(slide => {
				const target = slide.querySelector(selector);
				if (!target) return null;

				const s = slide.getBoundingClientRect();
				if (s.width === 0) return null; // verborgen/afgebroken slide overslaan

				let off = (s.left + s.width / 2 - center) / half; // -1 (links) .. 0 .. 1 (rechts)
				off = Math.max(-1, Math.min(1, off));

				const theta = off * thetaMaxRad; // positie op de cirkel (radialen)
				const rot = sign * theta * tiltRatio * (180 / Math.PI); // zichtbare tilt: fractie van theta
				const arcY = radius * (1 - Math.cos(theta)); // boog: rechtstreeks uit dezelfde hoek -> altijd >= 0

				return { target, rot, arcY };
			});

			// Pass 2: pas nu schrijven, via CSS-variabelen zodat de :hover-regel in
			// _team.scss (die de kaart rechtzet) de transform kan blijven overschrijven.
			updates.forEach(u => {
				if (!u) return;
				u.target.style.setProperty('--fan-rotate', `${u.rot.toFixed(2)}deg`);
				u.target.style.setProperty('--fan-y', `${u.arcY.toFixed(2)}px`);
			});

			if (running) rafId = requestAnimationFrame(tick);
		};

		const start = () => {
			if (running) return;
			running = true;
			rafId = requestAnimationFrame(tick);
		};

		const stop = () => {
			running = false;
			if (rafId !== null) cancelAnimationFrame(rafId);
			rafId = null;
		};

		// Alleen draaien terwijl de swiper in beeld is -- bespaart cycles verderop op de pagina.
		const observer = new IntersectionObserver(entries => {
			entries.forEach(entry => (entry.isIntersecting ? start() : stop()));
		}, { threshold: 0 });
		observer.observe(element);

		// Tikken op een kaart (bv. mobiel, waar :hover niet bestaat) pauzeert de
		// autoplay en toont de content via de .is-active klasse. Swiper's eigen
		// 'click'-event onderscheidt een tik van een sleep-gebaar, dus dit botst
		// niet met het slepen door de carousel. Nogmaals tikken op dezelfde kaart
		// sluit 'm weer en hervat de autoplay; op een andere kaart tikken wisselt door.
		swiper.on('click', (_swiper, event) => {
			const card = event.target.closest(selector);
			const activeCard = element.querySelector(`${selector}.is-active`);

			if (activeCard && activeCard !== card) {
				activeCard.classList.remove('is-active');
			}

			if (!card) {
				if (activeCard) swiper.autoplay?.start();
				return;
			}

			const wasActive = card.classList.contains('is-active');
			card.classList.toggle('is-active', !wasActive);

			if (wasActive) {
				swiper.autoplay?.start();
			} else {
				swiper.autoplay?.stop();
			}
		});

		swiper.on('destroy', () => {
			stop();
			observer.disconnect();
		});
	}

	#buildScopedControls(container, settings) {
		if (settings.navigation) {
			const nextEls = [...container.querySelectorAll(settings.navigation.nextEl)];
			const prevEls = [...container.querySelectorAll(settings.navigation.prevEl)];

			if (nextEls.length && prevEls.length) {
				settings.navigation = { ...settings.navigation, nextEl: nextEls, prevEl: prevEls };
			} else {
				delete settings.navigation;
			}
		}

		const scrollbarEl = container.querySelector('.swiper-scrollbar');
		if (scrollbarEl) {
			settings.scrollbar = { el: scrollbarEl, hide: false };
		}

		const paginationEl = container.querySelector('.swiper-pagination');
		if (paginationEl) {
			settings.pagination = {
				el: paginationEl,
				clickable: true,
				renderBullet: (index, className) => `<span class="${className}"></span>`,
			};
		}
	}

	#hideNavigationIfNeeded(element, swiper, settings) {
		if (swiper.snapGrid?.length <= 1) {
			element.closest(settings.wrapperEl)?.classList.add('swiper-hide-navigation');
		}
	}

	// --- Autoplay in view ---

	#observeAutoplay(element, swiper) {
		swiper.autoplay.stop();

		const observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					swiper.autoplay.start();
				} else {
					swiper.autoplay.stop();
				}
			});
		}, { threshold: 0 });

		observer.observe(element);
	}

	// --- Play/pause controls ---

	#bindSliderControl(container, swiper) {
		const control = container.querySelector('[data-slider-control]');
		if (!control) return;

		swiper.once('slideChange', () => {
			control.style.opacity = '1';
		});

		control.addEventListener('click', e => {
			e.preventDefault();
			this.#toggleSlider(control, swiper);
		});
	}

	// --- Slide titles next to nav arrows ---

	#bindSlideTitles(container, swiper) {
		const nextTitleEl = container.querySelector('[data-slide-title-next]');
		const prevTitleEl = container.querySelector('[data-slide-title-prev]');

		if (!nextTitleEl && !prevTitleEl) return;

		const updateTitles = () => {
			if (nextTitleEl) nextTitleEl.textContent = swiper.slides[swiper.activeIndex]?.dataset.title ?? '';
			if (prevTitleEl) prevTitleEl.textContent = swiper.slides[swiper.activeIndex - 1]?.dataset.title ?? '';
		};

		updateTitles();
		swiper.on('slideChange', updateTitles);
	}

	#toggleSlider(control, swiper) {
		if (swiper.autoplay.running) {
			swiper.autoplay.stop();
			control.classList.add('swiper-is-paused');
			control.classList.remove('swiper-is-playing');
		} else {
			swiper.autoplay.start();
			control.classList.add('swiper-is-playing');
			control.classList.remove('swiper-is-paused');
		}
	}

	// --- Slide cloning (minSlides) ---

	#cloneSlides(element, minSlides) {
		const wrapper = element.querySelector('.swiper-wrapper');
		if (!wrapper) return;

		const slideCount = wrapper.querySelectorAll('.swiper-slide').length;
		if (slideCount === 0) return;

		const cloneCount = Math.ceil(minSlides / slideCount);
		const originalHtml = wrapper.innerHTML;

		for (let i = 1; i < cloneCount; i++) {
			wrapper.insertAdjacentHTML('beforeend', originalHtml);
		}
	}
}

export default SwiperCarousel;